/**
 * `/setup` (slice 1b spec §6.2): a hard wait after checkout, with no skip.
 * Two numbered cards: the computer's setup steps, live, then Connect your
 * AI once it's ready, with a headline that says where things are. It sits
 * outside the shell, so it opens its own hub socket; everything it shows
 * comes from `/api/me`, which a `computer` event refetches. Once the AI is
 * connected it says so, then goes to Chats (the guard lets it linger).
 */
import { Button } from "@crew/ui/components/button";
import { Lock, RotateCw } from "lucide-react";
import type { ReactNode } from "react";
import { errorHint } from "../../app/api";
import { useHubEvents } from "../../app/events";
import type { Me } from "../../app/me";
import { useLogOut } from "../auth/logout";
import { LogOutFailed } from "../auth/logout-controls";
import { useRetryComputer } from "../computer/retry";
import { Checklist, FailureBox, SetupCard, SetupCardFooter } from "./checklist";
import { useNow, useSetupConnect, type SetupConnectState } from "./hooks";
import { elapsed, errorParts, setupPhase } from "./onboarding";
import { SetupConnect } from "./setup-connect";
import {
  connectDone,
  connectTitle,
  setupIntro,
  stepsCard,
  stepsNote,
  type Phase,
} from "./setup-view";

type Computer = NonNullable<Me["computer"]>;

/** The workspace, and who's signed in with Sign out: no sidebar here. */
function TopBar({ me }: { me: Me }) {
  const logOut = useLogOut();
  return (
    <header className="flex items-center justify-between gap-4 px-6 py-6 sm:px-10">
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          aria-hidden
          className="flex size-7 shrink-0 items-center justify-center rounded-md bg-foreground text-sm font-semibold text-background"
        >
          {me.workspace.name.trim().charAt(0).toUpperCase()}
        </span>
        <span className="truncate text-sm font-semibold text-foreground">{me.workspace.name}</span>
      </div>
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-4 text-meta leading-4.5">
          <span className="hidden text-muted-foreground sm:inline">{me.user.email}</span>
          <Button
            variant="link"
            className="h-auto p-0 text-meta font-medium text-copy"
            onClick={() => logOut.mutate()}
            disabled={logOut.isPending}
          >
            Sign out
          </Button>
        </div>
        <LogOutFailed logOut={logOut} className="text-meta text-destructive" />
      </div>
    </header>
  );
}

function Intro({ eyebrow, title, lead }: { eyebrow: string; title: string; lead: string }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-meta leading-4.5 text-muted-foreground">{eyebrow}</p>
      <h1 className="text-[34px] leading-10 tracking-[-0.02em] text-foreground sm:text-[44px] sm:leading-12.5">
        {title}
      </h1>
      <p className="text-lead text-copy">{lead}</p>
    </div>
  );
}

function Elapsed({ since }: { since: string }) {
  const now = useNow();
  return (
    <span className="text-meta leading-4.5 text-muted-foreground tabular-nums">
      {elapsed(since, now)} elapsed
    </span>
  );
}

/** Retry for the owner, with what it does; members wait for the owner. */
function SetupRetry({ owner }: { owner: boolean }) {
  const retry = useRetryComputer();
  if (!owner) {
    return <p className="text-meta text-muted-foreground">The workspace owner can retry.</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => retry.mutate()} disabled={retry.isPending}>
          <RotateCw aria-hidden />
          {retry.isPending ? "Retrying…" : "Retry"}
        </Button>
        <span className="text-meta leading-4.5 text-muted-foreground">
          Starts over from the first step. About 4 minutes.
        </span>
      </div>
      {retry.error ? (
        <p role="alert" className="text-meta text-destructive">
          {errorHint(retry.error)}
        </p>
      ) : null}
    </div>
  );
}

/** The time so far while setting up, or that it stopped. */
function StepsAside({ phase, since }: { phase: Phase; since: string | null }) {
  if (phase === "failed") {
    return <span className="text-meta leading-4.5 font-medium text-destructive">Stopped</span>;
  }
  return since ? <Elapsed since={since} /> : null;
}

/** Why setup stopped, and Retry. */
function StepFailed({ error, owner }: { error: string; owner: boolean }) {
  return (
    <>
      <FailureBox {...errorParts(error)} />
      <SetupRetry owner={owner} />
    </>
  );
}

/** Card 1 while setting up (or stopped): the §3.1 checklist, the time so far, Retry. */
function ComputerSteps({ me, phase, owner }: { me: Me; phase: Phase; owner: boolean }) {
  const card = stepsCard(me.computer);
  return (
    <SetupCard
      number={1}
      title="Your computer"
      data-computer-status={card.status}
      aside={<StepsAside phase={phase} since={card.since} />}
    >
      <div className="px-5.5 pt-2 pb-3.5">
        <Checklist
          kind="step"
          rows={card.rows}
          failure={<StepFailed error={card.error} owner={owner} />}
        />
      </div>
      <SetupCardFooter note={stepsNote(phase, card.since, me.workspace.timezone)} />
    </SetupCard>
  );
}

/** Card 1 once the computer is ready: one line. */
function ComputerReady({ status }: { status: string }) {
  return (
    <SetupCard
      number={1}
      title="Your computer"
      data-computer-status={status}
      aside={<span className="text-meta leading-4.5 font-medium text-moss">Ready</span>}
    />
  );
}

/** Card 2 before the computer is ready: what comes next, locked. */
function ConnectLocked() {
  return (
    <div className="flex items-start gap-2 rounded-panel px-5.5 py-4.5 ring-1 ring-foreground/14">
      <span className="text-xs leading-5 font-semibold text-hint">2</span>
      <div className="flex grow basis-0 flex-col gap-1">
        <p className="text-body leading-5 font-semibold text-muted-foreground">Connect your AI</p>
        <p className="text-meta leading-[19px] text-muted-foreground">
          Next, once the computer is ready. Crew doesn't come with an AI: you'll sign in to ChatGPT
          or Grok, or paste a Claude setup token or an API key from Anthropic, OpenAI or OpenRouter.
        </p>
      </div>
      <Lock className="mt-0.5 size-4 shrink-0 text-hint" aria-label="Locked" />
    </div>
  );
}

function ConnectCard({
  ai,
  owner,
  state,
}: {
  ai: Computer["ai"];
  owner: boolean;
  state: SetupConnectState;
}): ReactNode {
  return (
    <SetupCard number={2} title={connectTitle(state.view)} label="Connect your AI" raised>
      {owner ? (
        <SetupConnect ai={ai} state={state} />
      ) : (
        <p className="px-5.5 pt-2 pb-5 text-body text-muted-foreground">
          The workspace owner connects it.
        </p>
      )}
    </SetupCard>
  );
}

/** Card 1 and card 2: the steps and a locked connect, or ready and the connect. */
function SetupCards({
  me,
  phase,
  owner,
  state,
}: {
  me: Me;
  phase: Phase;
  owner: boolean;
  state: SetupConnectState;
}) {
  const computer = me.computer;
  if (phase !== "ready" || !computer) {
    return (
      <>
        <ComputerSteps me={me} phase={phase} owner={owner} />
        <ConnectLocked />
      </>
    );
  }
  return (
    <>
      <ComputerReady status={computer.status} />
      <ConnectCard ai={computer.ai} owner={owner} state={state} />
    </>
  );
}

export function SetupPage({ me }: { me: Me }) {
  useHubEvents(true);
  const owner = me.user.role === "owner";
  const phase = setupPhase(me.computer);
  const state = useSetupConnect(me.computer?.ai ?? null, connectDone(phase, me.computer));
  const intro = setupIntro(phase, state.view, {
    provider: state.provider,
    name: me.user.name,
    owner,
  });
  return (
    <div className="min-h-svh bg-background">
      <TopBar me={me} />
      <main className="mx-auto flex w-full max-w-160 flex-col gap-7 px-6 pt-4 pb-20 sm:pt-10">
        <Intro {...intro} />
        <SetupCards me={me} phase={phase} owner={owner} state={state} />
      </main>
    </div>
  );
}
